/**
 * User Management Page
 * ─────────────────────────────────────────────────────────────────────────────
 * Create users, assign roles + plant, activate/deactivate, reset passwords.
 */
import { useState } from 'react'
import {
  Button, Form, Input, Select, Tag, Space, App, Popconfirm,
  Modal, Divider, Badge, Checkbox, Alert,
} from 'antd'
import {
  PlusOutlined, EditOutlined, StopOutlined, CheckOutlined,
  KeyOutlined, SafetyOutlined, DeleteOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, rolesApi } from '@/features/users/api'
import type { User } from '@/shared/types/auth'
import { usePlantId } from '@/shared/hooks/usePlantId'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { useAuthStore } from '@/features/auth/store'

const ROLE_COLORS: Record<string, string> = {
  // System
  SYSTEM_ADMIN:         'red',
  IT_SUPPORT:           'magenta',
  // Management
  CHAIRMAN:             'gold',
  CEO:                  'gold',
  COO:                  'gold',
  CFO:                  'gold',
  // Finance
  ACCOUNTS_MANAGER:     'green',
  ACCOUNTS_EXECUTIVE:   'green',
  ACCOUNTANT:           'lime',
  CASHIER:              'lime',
  // Purchase
  PURCHASE_MANAGER:     'blue',
  PURCHASE_OFFICER:     'blue',
  // Stores
  STORES_MANAGER:       'purple',
  GRN_INCHARGE:         'geekblue',
  STORE_KEEPER:         'purple',
  GATE_ENTRY_OPERATOR:  'cyan',
  // Quality
  QC_MANAGER:           'green',
  QC_LAB_TECHNICIAN:    'lime',
  // Production
  PLANT_HEAD:           'volcano',
  PRODUCTION_MANAGER:   'volcano',
  SHIFT_SUPERVISOR:     'orange',
  FLOOR_OPERATOR:       'orange',
  MIXING_INCHARGE:      'orange',
  // Maintenance
  MAINTENANCE_MANAGER:  'red',
  MAINTENANCE_ENGINEER: 'orange',
  // Sales
  SALES_MANAGER:        'blue',
  SALES_EXECUTIVE:      'cyan',
  DISPATCH_OFFICER:     'cyan',
  // HR
  HR_MANAGER:           'purple',
  HR_EXECUTIVE:         'purple',
  PAYROLL_OFFICER:      'purple',
  // Security
  SECURITY_SUPERVISOR:  'red',
  SECURITY_GUARD:       'volcano',
  // Audit / Legacy
  VIEWER_AUDITOR:       'default',
  ERP_COORDINATOR:      'default',
  MASTER_DATA_MANAGER:  'default',
  MASTER_DATA_EXECUTIVE:'default',
}

export default function UsersPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plants } = usePlantId()

  // ── Create / Edit user form ────────────────────────────────────────────────
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen]   = useState(false)
  const [editing, setEditing]         = useState<User | null>(null)
  const [page, setPage]               = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch]           = useState('')

  // ── Assign Role modal ─────────────────────────────────────────────────────
  const [roleForm] = Form.useForm()
  const [roleTarget, setRoleTarget]   = useState<User | null>(null)
  const [roleModalOpen, setRoleModalOpen] = useState(false)

  // ── Reset Password modal ──────────────────────────────────────────────────
  const [pwForm] = Form.useForm()
  const [pwTarget, setPwTarget]       = useState<User | null>(null)
  const [pwModalOpen, setPwModalOpen] = useState(false)

  const canCreate     = usePermission('user.create')
  const canUpdate     = usePermission('user.update')
  const canDeactivate = usePermission('user.deactivate')
  const canDelete     = usePermission('user.delete')
  const canAssignRole = usePermission('role.assign')
  // Emergency direct grant (?emergency=true) is a SYSTEM_ADMIN-only
  // backend escape hatch — only show the checkbox to them.
  const isSysAdmin = useAuthStore(
    s => s.user?.roles?.includes('SYSTEM_ADMIN') ?? false,
  )

  // Sprint 0148 — Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  const [deleteReason, setDeleteReason] = useState('')

  // ── Data queries ───────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['users', page, pageSize, search],
    queryFn: () => usersApi.list({ page, page_size: pageSize, search: search || undefined }),
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['roles-list'],
    queryFn: rolesApi.list,
  })

  // ── Mutations ──────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (values: {
      username?: string; email: string; full_name: string
      employee_code?: string; password?: string
      role_ids?: string[]; plant_id?: string
    }) => {
      if (editing) {
        return usersApi.update(editing.id, {
          email: values.email,
          full_name: values.full_name,
          employee_code: values.employee_code,
        })
      } else {
        const user = await usersApi.create({
          email: values.email,
          username: values.username!,
          full_name: values.full_name,
          employee_code: values.employee_code,
          password: values.password!,
        })
        // Assign role + plant immediately after creation
        if (values.role_ids?.length) {
          await usersApi.assignRoles(user.id, {
            role_ids: values.role_ids,
            plant_id: values.plant_id,
          })
        }
        return user
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      message.success(editing ? 'User updated' : 'User created successfully')
      setDrawerOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const assignRoleMutation = useMutation({
    mutationFn: (values: {
      role_ids: string[]; plant_id?: string; emergency?: boolean
    }) =>
      usersApi.assignRoles(
        roleTarget!.id,
        { role_ids: values.role_ids, plant_id: values.plant_id },
        values.emergency ?? false,
      ),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      // GOV-001 P5 — grants apply only after ERP Coordinator approval.
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Role grant sent for approval')
      } else {
        message.success('Role assigned successfully')
      }
      setRoleModalOpen(false)
    },
    onError: (e: any) =>
      message.error(
        e?.response?.data?.detail ?? 'Role assignment failed', 6),
  })

  const resetPwMutation = useMutation({
    mutationFn: (values: { password: string }) =>
      usersApi.resetPassword(pwTarget!.id, values.password),
    onSuccess: () => {
      message.success('Password reset successfully')
      setPwModalOpen(false)
      pwForm.resetFields()
    },
    onError: () => message.error('Password reset failed'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? usersApi.deactivate(id) : usersApi.activate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      message.success('User status updated')
    },
    onError: () => message.error('Update failed'),
  })

  // Sprint 0148 — soft-delete mutation. Differs from deactivate:
  // permanent (no UI un-delete), hides from default list, captures
  // reason for audit. The user row stays in DB so audit-trail FK
  // references remain intact.
  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      usersApi.delete(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      message.success('User deleted (hidden from list, history preserved)')
      setDeleteTarget(null); setDeleteReason('')
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      message.error(e?.response?.data?.detail ?? 'Delete failed'),
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    setDrawerOpen(true)
  }

  const openEdit = (record: User) => {
    setEditing(record)
    form.setFieldsValue({
      email: record.email,
      full_name: record.full_name,
    })
    setDrawerOpen(true)
  }

  const openAssignRole = (record: User) => {
    setRoleTarget(record)
    roleForm.setFieldsValue({
      role_ids: record.roles.map(r => r.id),
      emergency: false,
    })
    setRoleModalOpen(true)
  }

  const openResetPw = (record: User) => {
    setPwTarget(record)
    pwForm.resetFields()
    setPwModalOpen(true)
  }

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (v: string, r: User) => (
        <Space>
          <Badge status={r.is_active ? 'success' : 'error'} />
          <strong>{v}</strong>
        </Space>
      ),
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'Employee Code',
      dataIndex: 'employee_code',
      key: 'employee_code',
      render: (v?: string) => v ?? '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles?: { id: string; role_code: string; role_name: string }[]) =>
        roles?.length
          ? roles.map(r => (
              <Tag key={r.id} color={ROLE_COLORS[r.role_code] ?? 'blue'}>
                {r.role_name}
              </Tag>
            ))
          : <Tag color="warning">No Role</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: User) => (
        <Space size={4} wrap>
          {canUpdate && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
              Edit
            </Button>
          )}
          {canAssignRole && (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<SafetyOutlined />}
              onClick={() => openAssignRole(record)}
            >
              Role
            </Button>
          )}
          {canUpdate && (
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => openResetPw(record)}
            >
              Reset Password
            </Button>
          )}
          {canDeactivate && (
            <Popconfirm
              title={record.is_active ? 'Deactivate this user?' : 'Activate this user?'}
              onConfirm={() =>
                toggleActiveMutation.mutate({ id: record.id, active: record.is_active })
              }
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                danger={record.is_active}
                icon={record.is_active ? <StopOutlined /> : <CheckOutlined />}
              >
                {record.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </Popconfirm>
          )}
          {canDelete && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => { setDeleteTarget(record); setDeleteReason('') }}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle="Create users, assign roles and plant access"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New User
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by name or username…"
          allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }}
          style={{ width: 300 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }}
      />

      {/* ── Create / Edit User Drawer ────────────────────────────────────── */}
      <FormDrawer
        title={editing ? 'Edit User' : 'New User'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => saveMutation.mutate(v))}
        loading={saveMutation.isPending}
        form={form}
      >
        {!editing && (
          <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required' }]}>
            <Input autoComplete="off" placeholder="e.g. rajesh.purchase" />
          </Form.Item>
        )}
        <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Rajesh Kumar" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true }, { type: 'email' }]}
        >
          <Input autoComplete="off" placeholder="e.g. rajesh@company.com" />
        </Form.Item>
        <Form.Item name="employee_code" label="Employee Code">
          <Input placeholder="e.g. EMP001" autoComplete="off" />
        </Form.Item>
        {!editing && (
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true },
              { min: 8, message: 'Minimum 8 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        )}

        {!editing && (
          <>
            <Divider orientation="left" style={{ fontSize: 13 }}>Role & Plant Assignment</Divider>
            <Form.Item name="role_ids" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
              <Select
                mode="multiple"
                placeholder="Select role(s)"
                options={roles.map(r => ({ value: r.id, label: r.role_name }))}
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="plant_id" label="Plant">
              <Select
                placeholder="Select plant"
                allowClear
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name ?? p.id }))}
              />
            </Form.Item>
          </>
        )}
      </FormDrawer>

      {/* ── Assign Role Modal ────────────────────────────────────────────── */}
      <Modal
        title={`Assign Role — ${roleTarget?.full_name}`}
        open={roleModalOpen}
        onCancel={() => setRoleModalOpen(false)}
        onOk={() =>
          roleForm.validateFields().then(v => assignRoleMutation.mutate(v))
        }
        okText="Save"
        confirmLoading={assignRoleMutation.isPending}
      >
        <Form form={roleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="role_ids"
            label="Role"
            rules={[{ required: true, message: 'Please select at least one role' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select role(s)"
              options={roles.map(r => ({ value: r.id, label: r.role_name }))}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="plant_id" label="Plant">
            <Select
              placeholder="Select plant"
              allowClear
              options={plants.map((p: any) => ({ value: p.id, label: p.plant_name ?? p.name }))}
            />
          </Form.Item>
          {isSysAdmin && (
            <>
              <Form.Item name="emergency" valuePropName="checked" noStyle>
                <Checkbox>
                  Emergency override — apply immediately without approval
                </Checkbox>
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) =>
                  getFieldValue('emergency') ? (
                    <Alert
                      style={{ marginTop: 8 }}
                      type="warning"
                      showIcon
                      message="This bypasses the ERP Coordinator approval and is audit-logged as EMERGENCY_DIRECT_GRANT under your name."
                    />
                  ) : null
                }
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* ── Reset Password Modal ─────────────────────────────────────────── */}
      <Modal
        title={`Reset Password — ${pwTarget?.full_name}`}
        open={pwModalOpen}
        onCancel={() => setPwModalOpen(false)}
        onOk={() =>
          pwForm.validateFields().then(v => resetPwMutation.mutate(v))
        }
        okText="Reset Password"
        confirmLoading={resetPwMutation.isPending}
      >
        <Form form={pwForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true },
              { min: 8, message: 'Minimum 8 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve()
                  return Promise.reject('Passwords do not match')
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sprint 0148 — Delete (soft) confirmation modal */}
      <Modal
        title={deleteTarget ? `Delete user "${deleteTarget.username}"?` : ''}
        open={!!deleteTarget}
        onCancel={() => { setDeleteTarget(null); setDeleteReason('') }}
        onOk={() => deleteTarget && deleteMutation.mutate({
          id: deleteTarget.id,
          reason: deleteReason,
        })}
        okText="Delete user"
        okButtonProps={{
          danger: true,
          loading: deleteMutation.isPending,
        }}
      >
        <p style={{ marginBottom: 8 }}>
          Hides this user from the default list and blocks login. Audit
          trail history (who created what, who approved what) is preserved
          — the user row stays in the database.
        </p>
        <p style={{ marginBottom: 8, color: '#fa8c16' }}>
          <b>Cannot be undone via the UI.</b> If you need to restore,
          recreate the user with a new password.
        </p>
        <p style={{ marginBottom: 4 }}>Reason (recommended for audit):</p>
        <Input.TextArea
          rows={3}
          placeholder="e.g. Employee separated on 2026-06-01 — left the mill."
          value={deleteReason}
          onChange={(e) => setDeleteReason(e.target.value)}
        />
      </Modal>
    </div>
  )
}
